import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { format, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';
import { Wifi, Tv, CreditCard, MessageSquare, Calendar, Clock } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

function ServiceCard({ icon: Icon, title, data, color }) {
  if (!data) return null;
  const daysLeft = data.expiryDate ? differenceInDays(new Date(data.expiryDate), new Date()) : null;

  return (
    <div className="card" style={{ border: `1px solid ${color}30` }}>
      <div className="card-header" style={{ background: `${color}08` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={18} color={color} />
          <h3 style={{ color }}>{title}</h3>
        </div>
        <span className={`badge badge-${data.isActive ? 'success' : 'warning'}`}>
          {data.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>
      <div className="card-body">
        {data.plan && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{data.plan.displayName || data.plan.name}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>
              {data.plan.speed?.download}/{data.plan.speed?.upload} Mbps · ₹{data.plan.price}/month
            </div>
          </div>
        )}
        {data.package && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{data.package.displayName || data.package.name}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '.82rem' }}>₹{data.package.price}/month</div>
          </div>
        )}
        {data.expiryDate && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid var(--border)' }}>
            <Calendar size={14} color="var(--text-muted)" />
            <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>
              Expires: <strong>{format(new Date(data.expiryDate), 'dd MMM yyyy')}</strong>
            </span>
            {daysLeft !== null && (
              <span className={`badge badge-${daysLeft < 0 ? 'danger' : daysLeft <= 3 ? 'danger' : daysLeft <= 7 ? 'warning' : 'success'}`}>
                {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const [customer, setCustomer] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await authAPI.me();
        setCustomer(data.data);
      } catch {
        toast.error('Failed to load account data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: '1.3rem', fontWeight: 700 }}>Welcome back! 👋</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '.9rem', marginTop: 4 }}>
          Manage your broadband and CATV services
        </p>
      </div>

      {/* Quick actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
        {[
          { icon: CreditCard, label: 'View Invoices', to: '/portal/payments', color: '#2563eb', bg: '#dbeafe' },
          { icon: MessageSquare, label: 'Raise Complaint', to: '/portal/complaints', color: '#dc2626', bg: '#fee2e2' },
          { icon: Clock, label: 'Track Tickets', to: '/portal/complaints', color: '#d97706', bg: '#fef3c7' },
        ].map(a => (
          <Link key={a.label} to={a.to} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 16,
            background: a.bg, borderRadius: 10, textDecoration: 'none', transition: 'transform .15s'
          }}>
            <a.icon size={24} color={a.color} />
            <span style={{ fontSize: '.82rem', fontWeight: 600, color: a.color }}>{a.label}</span>
          </Link>
        ))}
      </div>

      {/* Account info */}
      <div className="card mb-6">
        <div className="card-header"><h3>Account Information</h3></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Name</div>
              <div style={{ fontWeight: 600 }}>{customer?.name}</div>
            </div>
            <div>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Mobile</div>
              <div style={{ fontWeight: 600 }}>{customer?.mobile}</div>
            </div>
            <div>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Account Status</div>
              <span className={`badge badge-${customer?.status === 'active' || !customer?.status ? 'success' : customer?.status === 'suspended' ? 'warning' : 'danger'}`} style={{ textTransform: 'capitalize' }}>
                {customer?.status || 'active'}
              </span>
            </div>
            <div>
              <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Account Type</div>
              <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{customer?.role}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact ISP */}
      <div style={{ padding: 16, background: 'var(--primary-light)', borderRadius: 10, fontSize: '.85rem', color: 'var(--primary)', border: '1px solid var(--primary)30' }}>
        <strong>📞 Need help?</strong> Contact our support team for billing, technical issues, or plan upgrades. Visit your nearest office or raise a support ticket above.
      </div>
    </div>
  );
}
